if (typeof QuickAddToCart !== 'function') {

  class QuickAddToCart extends HTMLElement {
    constructor() {
      super()
      this.locationPromise = null
      this.productForm = this.querySelector('product-form')
      this.variantSelector = this.querySelector('variant-selector')
      this.defaultAddToCartText = this.getAttribute('data-add-to-cart-text')
      this.defaultOutOfStockText = this.getAttribute('data-out-of-stock-text')
      if (this.productForm) {
        this.addToCart = this.productForm.querySelector('[type="submit"]')
        this.addToCartText = this.addToCart.querySelector('.button__text')
        this.storeSelector = document.querySelector('store-selector[data-main-selector]')
        this.init()
      }
    }

    async init() {
      this.productForm.addEventListener('add-to-cart', () => {
        if (!document.body.classList.contains('template-cart')) {
          document.getElementById('site-cart-sidebar')?.show?.()
        }
        if (document.getElementById('cart-recommendations')) {
          document.getElementById('cart-recommendations').generateRecommendations()
        }
        document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
          const total = document.querySelector('#AjaxCartForm [data-cart-total]')
          if (total) elm.textContent = total.textContent
        })
        document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
          const count = document.querySelector('#AjaxCartForm [data-cart-count]')
          if (count) elm.textContent = count.textContent
        })
      })

      document.addEventListener('storechanged', this.onStoreChanged.bind(this))

      this.addEventListener('variant-selector:changed', this.onVariantChanged.bind(this))

      // Liquid provides the immediate online state. Refine store inventory
      // only for cards near the viewport or when a shopper interacts.
      this.refreshAvailability()

      if (this.dataset.onlineInventoryOnly === 'true') return

      const requestInventory = () => this.ensureLocation()
      this.addEventListener('pointerenter', requestInventory, { once: true, passive: true })
      this.addEventListener('focusin', requestInventory, { once: true })

      if ('IntersectionObserver' in window) {
        this.inventoryObserver = new IntersectionObserver((entries, observer) => {
          if (!entries[0]?.isIntersecting) return
          observer.unobserve(this)
          this.ensureLocation()
        }, { rootMargin: '400px 0px' })
        this.inventoryObserver.observe(this)
      } else {
        this.ensureLocation()
      }
    }

    async ensureLocation() {
      if (this.dataset.onlineInventoryOnly === 'true') {
        this.refreshAvailability()
        return null
      }
      if (!this.locationPromise) this.locationPromise = this.getLocation()
      const loaded = await this.locationPromise.catch(() => false)
      if (!loaded) this.locationPromise = null
      this.refreshAvailability()
      return this.locationPromise
    }

    refreshAvailability() {
		this.getVariantData()
		this.updateUnavailableVariants()
		if (!this.preserveSelection) this.selectFirstAvailableVariant()
		this.syncVariantPresentation()
		this.checkAddToCart()
    }

    async getLocation() {
      try {
        const token = window.KROWN.settings.storefront_api_key;
        const domain = window.KROWN.settings.store_domain;
        const productId = this.getAttribute('data-product-id');
		if ( !productId ) return false;

		const storefrontProductId = String(productId).startsWith('gid://shopify/Product/')
			? String(productId)
			: `gid://shopify/Product/${productId}`;
       
            const data = await window.ShopSanteInventory.load({
              productId: storefrontProductId,
              domain,
              token,
            })

			if (!data?.data?.product) return false;
        this.locationProduct = data   
		return true;

      } catch (error) {
		return false;
      }
    }

    getStoreName() {
      return window.ShopSanteInventory.storeName(this.locationProduct?.data?.product)
    }

    getVariantData() {
      const variantJson = this.querySelector('script[type="application/json"]:not([data-js-origin-variant-inventory-policy])')
      if (this.variantJsonNode !== variantJson) {
        this.variantJsonNode = variantJson
        try { this.liquidVariants = JSON.parse(variantJson?.textContent || '[]') } catch (_) { this.liquidVariants = [] }
      }
      const variants = this.liquidVariants || []
      const apiVariants = this.locationProduct?.data?.product?.variants?.nodes || []
      const store = this.getStoreName()
      this.variantData = variants.map(variant => {
        if (this.dataset.onlineInventoryOnly === 'true') {
          const inventory = this.querySelector(`[data-variant-id="${variant.id}"]`)
            || (String(this.dataset.variantId) === String(variant.id) ? this : null)
          const quantity = inventory?.dataset.inventoryPolicy === 'continue' && variant.available
            ? Infinity : Math.max(0, Math.floor(Number(inventory?.dataset.onlineInventoryQuantity) || 0))
          return { ...variant, available: variant.available === true && quantity > 0, quantity }
        }
        const item = apiVariants.find(item => String(item.id).split('/').pop() === String(variant.id))
        const state = window.ShopSanteInventory.state(item, store)
        let restricted = this.querySelector(`[data-online-hidden="true"][data-variant-id="${variant.id}"]`)
        if (state.isBundle && restricted) { restricted.removeAttribute('data-online-hidden'); restricted.removeAttribute('disabled'); restricted.removeAttribute('aria-disabled'); restricted = null }
        return { ...variant, available: state.available && !restricted, quantity: state.quantity }
      })
      this.currentVariant = this.variantData.find(variant => String(variant.id) === this.getAttribute('data-variant-id')) || null
      return Boolean(this.locationProduct?.data?.product)
    }

    async onVariantChanged(event) {
      this.preserveSelection = true
      await this.ensureLocation()
      const { id } = event.detail
      this.setAttribute('data-variant-id', id)
      this.currentVariant = this.variantData.find((v) => v.id === Number(id))
      this.getStoreName()
      this.syncVariantPresentation()
      this.checkAddToCart()
    }

    async onStoreChanged() {
      this.preserveSelection = true
      if (this.dataset.onlineInventoryOnly === 'true') {
        this.refreshAvailability()
        return
      }
      this.locationPromise = null
      await this.ensureLocation()
      this.refreshAvailability()
    }

    updateUnavailableVariants() {
      if (this.variantSelector) {
        const items = this.variantSelector.querySelectorAll('[data-variant-id]')
        items.forEach(item => {
          const variantId = Number(item.getAttribute('data-variant-id'))
          const variant = this.variantData.find((v) => v.id === variantId)
          if (variant && variant.available) {
            item.classList.remove('disabled')
            item.hidden = false
            item.removeAttribute('aria-hidden')
            item.removeAttribute('aria-disabled')
          }
          else {
            item.classList.add('disabled')
            item.hidden = false
            item.removeAttribute('aria-hidden')
            item.setAttribute('aria-disabled', 'true')
          }
        })
      }
    }

    selectFirstAvailableVariant() {
      if (this.currentVariant?.available) return

      const firstAvailable = this.variantData?.find(variant => variant.available)
      if (!firstAvailable) return

      this.currentVariant = firstAvailable
      this.setAttribute('data-variant-id', String(firstAvailable.id))

      const input = this.querySelector('variant-selector [name="id"]')
      if (input) input.value = String(firstAvailable.id)

      const item = this.variantSelector?.querySelector(`[data-variant-id="${firstAvailable.id}"]`)
      if (item) {
        this.variantSelector.querySelectorAll('[data-variant-id]').forEach(option => option.classList.remove('selected'))
        item.classList.add('selected')
        const title = this.variantSelector.querySelector('summary [data-variant-title]')
        const label = this.variantSelector.querySelector('summary [data-variant-label]')
        if (title) title.textContent = item.getAttribute('data-variant-title') || ''
        if (label) label.textContent = item.getAttribute('data-variant-label') || ''
      }

      this.dispatchEvent(new CustomEvent('variant-selector:changed', {
        bubbles: true,
        detail: { id: firstAvailable.id }
      }))
    }

    syncVariantPresentation() {
      if (!this.currentVariant) return

      const card = this.closest('.product-item')
      if (!card) return

      const featuredImage = this.currentVariant.featured_image
        || this.currentVariant.featured_media?.preview_image
      const imageUrl = featuredImage?.src

      if (imageUrl) {
        const primaryImage = card.querySelector(
          '[data-js-replace="image"] .product-item__image-figure--primary img, [data-js-replace="image"] img'
        )
        if (primaryImage) {
          // Keep Liquid's responsive image during inventory refreshes. Only
          // replace it when the shopper actually selects a different image.
          const nextImage = new URL(imageUrl, window.location.origin)
          const currentImage = new URL(primaryImage.getAttribute('src') || imageUrl, window.location.origin)
          if (nextImage.pathname !== currentImage.pathname || !primaryImage.srcset) {
            const imageWidth = Number(featuredImage.width) || 1080
            const widths = [...new Set([240, 360, 480, 640, 840, 1080].map(width => Math.min(width, imageWidth)))]
            const sizedImage = (width) => {
              const url = new URL(nextImage)
              url.searchParams.set('width', String(width))
              return url.href
            }
            primaryImage.sizes ||= '(max-width: 767px) 45vw, (max-width: 1280px) 30vw, 420px'
            primaryImage.srcset = widths.map(width => `${sizedImage(width)} ${width}w`).join(', ')
            primaryImage.src = sizedImage(Math.min(480, imageWidth))
          }
          primaryImage.alt = featuredImage.alt
            || card.querySelector('.product-item__title')?.textContent?.trim()
            || ''
        }
      }

      card.querySelectorAll('a[href*="/products/"]').forEach(link => {
        try {
          const url = new URL(link.href, window.location.origin)
          if (url.searchParams.get('variant') === String(this.currentVariant.id)) return
          url.searchParams.set('variant', String(this.currentVariant.id))
          link.href = `${url.pathname}${url.search}${url.hash}`
        } catch (error) {
          // A malformed third-party link must never break variant selection.
        }
      })
    }

    checkAddToCart() {
      if (!this.currentVariant || !this.currentVariant.available) {
        if (this.addToCart) this.addToCart.disabled = true
        if (this.addToCartText) {
          this.addToCartText.textContent = this.defaultOutOfStockText
            || window.KROWN.settings.locales.products_sold_out_variant
        }
      } else {
        if (this.addToCart) this.addToCart.disabled = false
        if (this.addToCartText) {
          this.addToCartText.textContent = this.defaultAddToCartText
            || window.KROWN.settings.locales.products_add_to_cart_button
        }
      }
    }
  }

  if (typeof customElements.get('quick-add-to-cart') == 'undefined') {
    customElements.define('quick-add-to-cart', QuickAddToCart)
  }

}

if (typeof QuickViewProduct !== 'function') {

  class QuickViewProduct extends HTMLElement {
    constructor() {
      super()
      if (this.querySelector('a')) {
        this.init()
      }
    }
    init() {

      this.quickViewModal = null
      this.querySelector('a').addEventListener('click', (e) => {

        e.preventDefault()

        if (!this.quickViewModal) {

          const target = e.currentTarget

          target.classList.add('working')

          fetch(`${target.getAttribute('href')}${target.getAttribute('href').includes('?') ? '&' : '?'}view=quick-view`)
            .then(response => {
              if (!response.ok) throw new Error('Unable to load quick view')
              return response.text()
            })
            .then(text => {

              const quickViewHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('#product-quick-view')
              if (!quickViewHTML) throw new Error('Quick view content is missing')

              // create modal w content

              const quickViewContainer = document.createElement('div')
              quickViewContainer.innerHTML = `<modal-box id="modal-${target.dataset.id}"
								class="modal modal--product"
								data-options='{
									"enabled": false,
									"showOnce": false,
									"blockTabNavigation": true
								}'
								tabindex="-1" role="dialog" aria-modal="true"
							>
								<div class="container--medium">
									<div class="modal-content">
										<button class="modal-close" data-js-close data-js-first-focus style="position:absolute;margin:0;top:0;right:0">${window.KROWN.settings.symbols.close}</button>
									</div>
								</div>
								<span class="modal-background" data-js-close></span>
							</modal-box>`

              this.quickViewModal = quickViewContainer.querySelector('modal-box')
              document.body.appendChild(this.quickViewModal)

              this.quickViewModal.querySelector('.modal-content').innerHTML = quickViewHTML.innerHTML

              if (!window.productPageScripts) {

                const scripts = this.quickViewModal.querySelectorAll('script')
                scripts.forEach(elm => {
                  if (!elm.src) return
                  const script = document.createElement('script')
                  script.src = elm.src
                  script.defer = true
                  document.body.append(script)
                })
                window.productPageScripts = true

              }

              this.quickViewModal.querySelector('.product-quick-view__close').addEventListener('click', () => {
                this.quickViewModal.hide()
              })

              if (this.quickViewModal.querySelector('[data-js-product-form]')) {
                this.quickViewModal.querySelector('[data-js-product-form]').addEventListener('add-to-cart', () => {
                  this.quickViewModal.hide()
                })
              }

              setTimeout(() => {
                this.quickViewModal.show()
                target.classList.remove('working')
              }, 250)

            })
            .catch(error => {
              console.error(error)
              target.classList.remove('working')
            })

        } else {
          this.quickViewModal.show()
        }

      })
    }
  }

  if (typeof customElements.get('quick-view-product') == 'undefined') {
    customElements.define('quick-view-product', QuickViewProduct)
  }

}

if (typeof VariantSelector !== 'function') {
  class VariantSelector extends HTMLElement {
    constructor() {
      super()
      this.handleDocumentClick = null
      this.cleanupDropdown = null
      if (this.querySelector('details')) {
        this.init()
      }
    }

    connectedCallback() {
      if (this.handleDocumentClick) {
        document.addEventListener('click', this.handleDocumentClick)
      }
    }

    disconnectedCallback() {
      if (this.handleDocumentClick) {
        document.removeEventListener('click', this.handleDocumentClick)
      }
      this.cleanupDropdown?.()
    }

    init() {
      const input = this.querySelector('[name="id"]')
      const details = this.querySelector('details')
      const dropdown = details.querySelector('.variant-selector__dropdown')
      const productCard = this.closest('.product-item, .cart-upsell-item')
      const section = this.closest('.shopify-section')
      const supportsPopover = !!dropdown
        && typeof dropdown.showPopover === 'function'
        && typeof dropdown.hidePopover === 'function'
      let trackingFrame = null

      if (supportsPopover) {
        dropdown.setAttribute('popover', 'manual')
        details.classList.add('variant-selector--uses-popover')
      }

      const setDropdownOpenState = (isOpen) => {
        productCard?.classList.toggle('variant-dropdown-is-open', isOpen)

        if (!supportsPopover) {
          productCard?.classList.toggle('variant-dropdown-needs-containment-release', isOpen)
          section?.classList.toggle(
            'variant-dropdown-needs-containment-release',
            !!section.querySelector('.product-item .variant-selector details[open], .cart-upsell-item .variant-selector details[open]')
          )
        }
      }

      const showDropdownPopover = () => {
        if (supportsPopover && !dropdown.matches(':popover-open')) {
          dropdown.showPopover()
        }
      }

      const hideDropdownPopover = () => {
        if (supportsPopover && dropdown.matches(':popover-open')) {
          dropdown.hidePopover()
        }
      }

      const clearDropdownPosition = () => {
        details.removeAttribute('data-dropdown-positioned')
        details.style.removeProperty('--variant-dropdown-top')
        details.style.removeProperty('--variant-dropdown-left')
        details.style.removeProperty('--variant-dropdown-width')
      }

      const positionDropdown = () => {
        if (!details.open || !dropdown || !window.matchMedia('(min-width: 768px)').matches) {
          details.removeAttribute('data-dropdown-placement')
          clearDropdownPosition()
          return
        }

        const select = details.querySelector('summary')
        const selectRect = select.getBoundingClientRect()
        const desiredHeight = Math.min(
          dropdown.scrollHeight,
          window.innerHeight * 0.55,
          272
        )
        const roomBelow = window.innerHeight - selectRect.bottom - 16
        const roomAbove = selectRect.top - 16
        const placement = roomBelow >= desiredHeight || roomBelow >= roomAbove ? 'down' : 'up'
        const viewportGutter = 16
        const quickBuyRect = select.closest('.quick-add-to-cart__container')?.getBoundingClientRect()
        const rootFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16
        const minimumDropdownWidth = Math.min(
          rootFontSize * 14,
          window.innerWidth - (viewportGutter * 2)
        )
        const dropdownWidth = Math.min(
          Math.max(selectRect.width, quickBuyRect?.width || 0, minimumDropdownWidth),
          window.innerWidth - (viewportGutter * 2)
        )
        const dropdownLeft = Math.min(
          Math.max(selectRect.left, viewportGutter),
          window.innerWidth - dropdownWidth - viewportGutter
        )
        const dropdownTop = placement === 'down'
          ? selectRect.bottom + 8
          : Math.max(viewportGutter, selectRect.top - desiredHeight - 8)

        details.setAttribute('data-dropdown-placement', placement)

        if (!supportsPopover) {
          clearDropdownPosition()
          return
        }

        details.style.setProperty('--variant-dropdown-top', `${dropdownTop}px`)
        details.style.setProperty('--variant-dropdown-left', `${dropdownLeft}px`)
        details.style.setProperty('--variant-dropdown-width', `${dropdownWidth}px`)
        details.setAttribute('data-dropdown-positioned', '')
      }

      const scheduleDropdownPosition = (event) => {
        if (event?.target instanceof Node && dropdown.contains(event.target)) return
        if (trackingFrame !== null) return

        trackingFrame = window.requestAnimationFrame(() => {
          trackingFrame = null
          positionDropdown()
        })
      }

      const startTrackingDropdown = () => {
        window.addEventListener('resize', scheduleDropdownPosition, { passive: true })
        window.addEventListener('scroll', scheduleDropdownPosition, { passive: true, capture: true })
      }

      const stopTrackingDropdown = () => {
        window.removeEventListener('resize', scheduleDropdownPosition)
        window.removeEventListener('scroll', scheduleDropdownPosition, true)

        if (trackingFrame !== null) {
          window.cancelAnimationFrame(trackingFrame)
          trackingFrame = null
        }
      }

      this.cleanupDropdown = () => {
        stopTrackingDropdown()
        productCard?.classList.remove(
          'variant-dropdown-is-open',
          'variant-dropdown-needs-containment-release'
        )
        section?.classList.toggle(
          'variant-dropdown-needs-containment-release',
          !!section.querySelector('.product-item .variant-selector details[open], .cart-upsell-item .variant-selector details[open]')
        )
      }

      this.handleDocumentClick = (event) => {
        if (!details.contains(event.target) && details.open) {
          details.open = false
        }
      }

      details.addEventListener('toggle', () => {
        if (!details.open) {
          hideDropdownPopover()
          setDropdownOpenState(false)
          details.removeAttribute('data-dropdown-placement')
          clearDropdownPosition()
          stopTrackingDropdown()
          return
        }

        document.querySelectorAll('variant-selector details[open]').forEach(openDetails => {
          if (openDetails !== details) openDetails.open = false
        })

        setDropdownOpenState(true)
        showDropdownPopover()

        window.requestAnimationFrame(() => {
          positionDropdown()
          window.requestAnimationFrame(() => {
            positionDropdown()
            startTrackingDropdown()
          })
        })
      })

      details.addEventListener('click', (event) => {
        const item = event.target.closest('li')
        if (item) {
          if (item.hidden || item.classList.contains('disabled') || item.hasAttribute('disabled')) {
            event.preventDefault()
            return
          }
          const id = item.getAttribute('data-variant-id')
          const label = item.getAttribute('data-variant-label')
          const title = item.getAttribute('data-variant-title')
          this.querySelector('summary [data-variant-title]').textContent = title
          this.querySelector('summary [data-variant-label]').textContent = label

          const items = details.querySelectorAll('ul > li')

          items.forEach(item => {
            item.classList.remove('selected')
          })

          item.classList.add('selected')

          if (input) {
            input.value = id
          }
          details.open = false
          this.dispatchEvent(new CustomEvent('variant-selector:changed', {
            bubbles: true,
            detail: { id }
          }))
        }
      })
    }
  }

  if (typeof customElements.get('variant-selector') == 'undefined') {
    customElements.define('variant-selector', VariantSelector)
  }
}

if (typeof WishListButton !== 'function') {
  class WishListButton extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      // The wishlist app can overwrite labels with its default French copy.
      const localizeLabels = () => {
        const english = document.documentElement.lang.startsWith('en');
        this.querySelectorAll('button').forEach(button => {
          const remove = button.dataset.swaction === 'removeFromWishlist' || button.getAttribute('aria-pressed') === 'true';
          const label = english ? (remove ? 'Remove from wishlist' : 'Add to wishlist') : (remove ? 'Retirer de la liste de souhaits' : 'Ajouter à la liste de souhaits');
          if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
        });
      };
      this._labelObserver?.disconnect();
      this._labelObserver = new MutationObserver(localizeLabels);
      this._labelObserver.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-label', 'aria-pressed', 'data-swaction'] });
      localizeLabels();
      if (!window.SwymCallbacks) {
        window.SwymCallbacks = []
      }
      window.SwymCallbacks.push(this.init.bind(this))
    }

    disconnectedCallback() { this._labelObserver?.disconnect(); }

    init(swatInstance) {
      this.swat = swatInstance
      window.swat = swatInstance
      this.addButton = this.querySelector('[data-swaction="addToWishlist"]')
      this.removeButton = this.querySelector('[data-swaction="removeFromWishlist"]')
      if (this.removeButton) {
        this.removeButton.addEventListener('click', this.handleRemoveFromWishlist.bind(this))
      }
      this.updateVariant()
    }

    updateVariant() {
      const product = this.closest('.product-item')
      const button = this.querySelector('button')
      if (product) {
        product.addEventListener('variant-selector:changed', (event) => {
          button.setAttribute('data-variant-id', event.detail.id)
          this.swat.initializeActionButtons('body')
        })
      }
    }

    handleRemoveFromWishlist() {
      const epi = this.addButton.dataset.variantId
      this.swat.removeFromWishList({ epi }, (event) => {
        this.addButton.classList.value = 'add-to-wishlist'
        this.swat.initializeActionButtons('body')
      })
    }
  }

  if (typeof customElements.get('wish-list-button') == 'undefined') {
    customElements.define('wish-list-button', WishListButton)
  }
}

// The app also injects a standalone heart on product pages, outside wish-list-button.
if (document.documentElement.lang.startsWith('en') && !window.ShopSanteWishlistLabels) {
  const update = root => {
    const buttons = root.matches?.('button.swym-add-to-wishlist') ? [root] : Array.from(root.querySelectorAll?.('button.swym-add-to-wishlist') || []);
    buttons.forEach(button => {
      const label = button.getAttribute('aria-pressed') === 'true' ? 'Remove from wishlist' : 'Add to wishlist';
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
    });
  };
  const observer = new MutationObserver(records => records.forEach(record => {
    if (record.type === 'attributes') update(record.target);
    else record.addedNodes.forEach(node => { if (node.nodeType === 1) update(node); });
  }));
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-label', 'aria-pressed'] });
  update(document.body);
  window.ShopSanteWishlistLabels = observer;
}

if (!window.shopSanteWishlistNavigationReady) {
  window.shopSanteWishlistNavigationReady = true
  const wishlistPath = '/pages/wishlist'
  const english = document.documentElement.lang.toLowerCase().startsWith('en')
  const configureWishlistNavigation = (swat) => {
    if (window.SwymUtils) window.SwymUtils.getHostedURL = () => wishlistPath
    if (swat.ui) swat.ui.open = () => window.location.assign(wishlistPath)
  }
  if (window._swat?.ui) configureWishlistNavigation(window._swat)
  window.SwymCallbacks = window.SwymCallbacks || []
  window.SwymCallbacks.push(configureWishlistNavigation)

  const updateWishlistLinks = () => {
    document.querySelectorAll('.product-text .swym-tooltip .swym-open-ui').forEach((link) => {
      if (link.getAttribute('href') !== wishlistPath) link.setAttribute('href', wishlistPath)
      const label = english ? 'View my wishlist' : 'Voir mes favoris'
      if (link.textContent !== label) link.textContent = label
      const text = link.parentElement
      const added = english ? 'Added to your wishlist. ' : 'Ajouté à vos favoris. '
      for (const node of text.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() && node.textContent !== added) {
          node.textContent = added
        }
      }
    })
  }
  updateWishlistLinks()
  document.querySelectorAll('.product-text').forEach((root) => {
    new MutationObserver(updateWishlistLinks).observe(root, { childList: true, subtree: true })
  })
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('.swym-tooltip a.swym-open-ui')
    if (!link) return
    link.href = wishlistPath
    event.stopImmediatePropagation()
  }, true)
}
